import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@evaneos/b2b-ui';
import { compareDesc } from 'date-fns';

import { parseQueryString } from '../../shared/utilities/queryString';
import { useAuth } from '../../shared/providers/AuthProvider';
import { useWebSocket } from '../../shared/providers/WebSocketProvider';
import { sendErrorReport } from '../../shared/utilities/sendErrorReport';

import { getNotifications, markNotificationAsRead, markNotificationAsUnread } from './api';
import { allowedNotifications } from './contants';

const getNotificationsQueryKey = () => ['notifications'];

const useAllNotifications = () =>
    useQuery({
        queryKey: getNotificationsQueryKey(),
        queryFn: getNotifications,
        staleTime: 0,
    });

export const useFilteredNotifications = () => {
    const {
        data: notifications = [],
        status,
        isLoading,
        isError,
        isSuccess,
    } = useAllNotifications();
    const { userId: currentUserId } = useAuth();
    const location = useLocation();
    const searchQuery = parseQueryString(location.search);
    const agentIds = searchQuery.agentId;

    const filteredAndSortedNotifications = useMemo(() => {
        return notifications
            .filter((notification) => {
                const stringifiedNotificationAgentId = String(notification.subscriberId);

                if (agentIds === 'all') return true;

                if (Array.isArray(agentIds))
                    return agentIds.includes(stringifiedNotificationAgentId);

                if (agentIds) return stringifiedNotificationAgentId === agentIds;

                // If no agentIds are provided, show notifications for the current user
                return notification.subscriberId === Number(currentUserId);
            })
            .filter((notification) => allowedNotifications.includes(notification.type))
            .sort((a, b) => {
                if (!a.occurredOn || !b.occurredOn) return 0;
                return compareDesc(a.occurredOn, b.occurredOn);
            });
    }, [notifications, agentIds, currentUserId]);

    return {
        status,
        isLoading,
        isError,
        isSuccess,
        data: filteredAndSortedNotifications,
    };
};

export const useUpdateNotificationRead = () => {
    const { formatMessage } = useIntl();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            notificationId,
            action,
            options: _options = {},
        }: {
            notificationId: string;
            action: 'read' | 'unread';
            options?: {
                showSuccessToast?: boolean;
            };
        }) => {
            return action === 'read'
                ? markNotificationAsRead(notificationId)
                : markNotificationAsUnread(notificationId);
        },

        onSuccess: (_, variables) => {
            if (variables.options?.showSuccessToast !== false) {
                const translationKey =
                    variables.action === 'read'
                        ? 'homepage.notifications.mark_as_read_success'
                        : 'homepage.notifications.mark_as_unread_success';
                toast.success(formatMessage({ id: translationKey }));
            }
        },
        onError: () => {
            toast.error(formatMessage({ id: 'global.generic_error' }));
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: getNotificationsQueryKey() });
        },
    });
};

export function useRealTimeNotifications() {
    const { socket } = useWebSocket();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!socket || typeof window === 'undefined') {
            return;
        }

        const handleNotificationMessage = (event: MessageEvent) => {
            if (!event.data) return;

            if (typeof event.data === 'string') {
                try {
                    const response = JSON.parse(event.data);
                    const messageType = response.data.type;
                    if (messageType === 'notification') {
                        void queryClient.invalidateQueries({
                            queryKey: getNotificationsQueryKey(),
                        });
                    }
                } catch (e) {
                    sendErrorReport(e as Error);
                    throw Error('Socket message is not a JSON');
                }
            }
        };

        socket.addEventListener('message', handleNotificationMessage);

        return () => {
            socket.removeEventListener('message', handleNotificationMessage);
        };
    }, [queryClient, socket]);
}
